import React from 'react';
import { Check, ChevronRight, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

import { NodeShell, wasRecentlyDragged } from './NodeShell';
import { ActionPill, SvgRemove } from './ProductNode';

export const WALL_PICKER_NODE_WIDTH = 320;

export interface WallPickerWall {
    id: number;
    x: number;
    y: number;
    /** Grayscale value of this wall in the merged mask (0-255). Used to
     *  isolate just this wall's pixels for the hover highlight. */
    grayValue?: number;
}

interface WallPickerNodeProps {
    nodeId: string;
    x: number;
    y: number;
    zoom: number;
    selected?: boolean;
    /** Base interior image (the analyzed Space's source) shown under markers. */
    baseImageUrl?: string;
    /** Walls returned by the analysis (centroids in image-pixel coords). */
    walls: WallPickerWall[];
    /** Native dimensions of baseImageUrl — used to position markers as %s. */
    imageWidth: number;
    imageHeight: number;
    /** Wall ids the vendor has selected. */
    selectedWallIds: number[];
    /** True while the upstream analysis is still running. */
    pending?: boolean;
    /** True after Continue triggers a render-phase generation. */
    generating?: boolean;
    /** URL of the merged mask PNG; used to highlight the hovered wall. */
    maskUrl?: string;
    /**
     * Credit cost a Continue press will reserve for the render-phase
     * generation it spawns. Computed in the workspace via
     * resolveCreditCost(recipe, { inputs: { ...recipeInputs, phase: 'render' }, visualizationType }).
     * Shown next to the Continue label so the vendor knows what each
     * re-pick costs (the picker setup is already paid for upstream).
     */
    continueCreditCost?: number;
    /** Toggle a wall's selection state. */
    onToggleWall: (wallId: number) => void;
    /** Spawn a new render-phase Generation downstream. */
    onContinue: () => void;
    onDrag: (deltaX: number, deltaY: number) => void;
    onClick?: (modifiers: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) => void;
    onRemove?: () => void;
    /** Faded / non-interactive rendering in workspace select mode. */
    dimmed?: boolean;
}

/**
 * Build per-wall offscreen canvases that isolate each wall's pixels in
 * the merged mask, recoloured to a translucent tint that reads as a
 * hover highlight. Returns a map from wallId → data URL ready to use as
 * an <img> src. The mask is fetched once with CORS and cached; if the
 * fetch or canvas read fails (e.g. CORS not configured on the bucket),
 * the hook returns an empty map and the highlight just no-ops.
 */
function useWallMaskHighlight(
    maskUrl: string | undefined,
    walls: WallPickerWall[],
    highlightRgb: [number, number, number],
): Record<number, string> {
    const [overlays, setOverlays] = React.useState<Record<number, string>>({});

    React.useEffect(() => {
        setOverlays({});
        if (!maskUrl || walls.length === 0) return;
        let cancelled = false;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            if (cancelled) return;
            try {
                const w = img.naturalWidth;
                const h = img.naturalHeight;
                const sourceCanvas = document.createElement('canvas');
                sourceCanvas.width = w;
                sourceCanvas.height = h;
                const sctx = sourceCanvas.getContext('2d');
                if (!sctx) return;
                sctx.drawImage(img, 0, 0);
                const sourceData = sctx.getImageData(0, 0, w, h).data;

                const next: Record<number, string> = {};
                // Tolerance handles minor PNG quantization or anti-alias
                // bleed at the wall edges so the highlight covers the
                // full surface, not a perforated subset.
                const tol = 2;
                for (const wall of walls) {
                    if (typeof wall.grayValue !== 'number') continue;
                    const target = wall.grayValue;
                    const wallCanvas = document.createElement('canvas');
                    wallCanvas.width = w;
                    wallCanvas.height = h;
                    const wctx = wallCanvas.getContext('2d');
                    if (!wctx) continue;
                    const out = wctx.createImageData(w, h);
                    const od = out.data;
                    const [r, g, b] = highlightRgb;
                    for (let i = 0; i < sourceData.length; i += 4) {
                        // Mask is grayscale — read from any channel.
                        const v = sourceData[i];
                        if (Math.abs(v - target) <= tol) {
                            od[i] = r;
                            od[i + 1] = g;
                            od[i + 2] = b;
                            od[i + 3] = 140; // ~55% alpha
                        }
                    }
                    wctx.putImageData(out, 0, 0);
                    try {
                        next[wall.id] = wallCanvas.toDataURL('image/png');
                    } catch {
                        // Tainted canvas — bail on this wall.
                    }
                }
                if (!cancelled) setOverlays(next);
            } catch {
                /* getImageData throws on tainted canvas — silently no-op */
            }
        };
        img.onerror = () => { /* mask unreachable — silent no-op */ };
        img.src = maskUrl;
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maskUrl, walls.map((w) => `${w.id}:${w.grayValue}`).join('|')]);

    return overlays;
}

/**
 * Internal subcomponent — image with overlaid wall markers + optional
 * mask hover highlight. Used by both the on-canvas WallPickerNode body
 * (small) and the lightbox modal (large) so both views share identical
 * marker behavior.
 */
const WallPickerImageView: React.FC<{
    baseImageUrl?: string;
    walls: WallPickerWall[];
    imageWidth: number;
    imageHeight: number;
    selectedWallIds: number[];
    pending?: boolean;
    onToggleWall: (wallId: number) => void;
    /** Optional click handler for the image itself (used to open lightbox). */
    onImageClick?: () => void;
    /** Map wallId → data-URL overlay for hover highlight. */
    overlays: Record<number, string>;
    /** Marker size in px (smaller for node, larger for lightbox). */
    markerSize: number;
}> = ({
    baseImageUrl,
    walls,
    imageWidth,
    imageHeight,
    selectedWallIds,
    pending,
    onToggleWall,
    onImageClick,
    overlays,
    markerSize,
}) => {
    const { t } = useTranslation();
    const [hoveredWallId, setHoveredWallId] = React.useState<number | null>(null);

    const selectedSet = React.useMemo(
        () => new Set(selectedWallIds),
        [selectedWallIds],
    );
    const aspectRatio =
        imageWidth > 0 && imageHeight > 0
            ? `${imageWidth} / ${imageHeight}`
            : '1 / 1';

    const markerPositions = React.useMemo(() => {
        if (imageWidth <= 0 || imageHeight <= 0) return [];
        return walls.map((w) => ({
            id: w.id,
            leftPct: (w.x / imageWidth) * 100,
            topPct: (w.y / imageHeight) * 100,
        }));
    }, [walls, imageWidth, imageHeight]);

    const hoverOverlayUrl = hoveredWallId !== null ? overlays[hoveredWallId] : undefined;

    return (
        <div
            className={cn(
                'relative w-full overflow-hidden bg-muted bg-cover bg-center',
                // `pointer`, as every other clickable node image uses. `zoom-in`
                // said the picture was a thing to open and nothing else, over
                // the whole node, which is most of what made it feel stuck.
                onImageClick && !pending && 'cursor-pointer',
            )}
            style={{
                aspectRatio,
                backgroundImage: baseImageUrl ? `url(${baseImageUrl})` : undefined,
            }}
            onClick={(e: React.MouseEvent) => {
                if (!onImageClick || pending) return;
                // A click that ends a drag is not a click on the picture.
                if (wasRecentlyDragged()) return;
                // Don't bubble up to the canvas drag handler.
                e.stopPropagation();
                // Markers stop propagation themselves — this handler
                // fires only when the click landed on the bare image.
                onImageClick();
            }}
        >
            {pending && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/70 text-sm text-muted-foreground">
                    <Loader2 className="size-5 animate-spin" />
                    {t('CONTENT_STUDIO.WALL_PICKER_ANALYZING', 'Analyzing scene…')}
                </div>
            )}

            {!pending && hoverOverlayUrl && (
                <img
                    src={hoverOverlayUrl}
                    alt=""
                    // Rendering at native resolution avoids any pixel snapping
                    // mismatch with the underlying background-image scaling.
                    className="pointer-events-none absolute inset-0 h-full w-full [image-rendering:pixelated]"
                />
            )}

            {!pending && markerPositions.map((m) => {
                const isSelected = selectedSet.has(m.id);
                return (
                    <div
                        key={m.id}
                        onMouseEnter={() => setHoveredWallId(m.id)}
                        onMouseLeave={() => setHoveredWallId((h) => (h === m.id ? null : h))}
                        onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            onToggleWall(m.id);
                        }}
                        onMouseDown={(e: React.MouseEvent) => {
                            // Don't let NodeShell's drag handler eat the
                            // click intent on the marker.
                            e.stopPropagation();
                        }}
                        title={
                            isSelected
                                ? t('CONTENT_STUDIO.WALL_PICKER_DESELECT_TIP', 'Deselect this wall')
                                : t('CONTENT_STUDIO.WALL_PICKER_SELECT_TIP', 'Select this wall')
                        }
                        className={cn(
                            // Drop shadow keeps the marker legible over any photo.
                            'absolute z-[1] flex -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-2 font-medium shadow-[0_2px_6px_rgba(0,0,0,0.25)] transition-transform hover:scale-110',
                            isSelected
                                ? 'border-background bg-foreground text-background ring-[3px] ring-foreground/20'
                                : 'border-foreground bg-background/90 text-foreground',
                        )}
                        style={{
                            // Marker positions are image-pixel maths — LTR space.
                            left: `${m.leftPct}%`,
                            top: `${m.topPct}%`,
                            width: markerSize,
                            height: markerSize,
                            fontSize: Math.round(markerSize * 0.4),
                        }}
                    >
                        {isSelected ? (
                            <Check
                                size={Math.round(markerSize * 0.5)}
                                strokeWidth={3}
                            />
                        ) : (
                            String(m.id + 1)
                        )}
                    </div>
                );
            })}
        </div>
    );
};

/**
 * Canvas node for the lifestyle-scene WallPicker flow. Sits between a
 * Recipe node (lifestyle-scene + walls) and any Generation nodes the
 * vendor produces from it. Shows the analyzed base interior with one
 * circular marker per detected wall — vendors toggle which walls receive
 * the product texture, then click Continue to spawn a render-phase
 * Generation with that selection. The picker stays on the canvas so the
 * vendor can revisit it, change the selection, and spawn another render
 * sibling for comparison.
 *
 * Clicking the image opens a lightbox modal at proper size so the
 * vendor can pick walls comfortably; hovering a marker tints the whole
 * surface using the segmentation mask so the vendor knows exactly which
 * area each marker represents.
 */
export const WallPickerNode: React.FC<WallPickerNodeProps> = ({
    nodeId,
    x,
    y,
    zoom,
    selected,
    baseImageUrl,
    walls,
    imageWidth,
    imageHeight,
    selectedWallIds,
    pending,
    generating,
    maskUrl,
    continueCreditCost,
    onToggleWall,
    onContinue,
    onDrag,
    onClick,
    onRemove,
    dimmed,
}) => {
    const { t } = useTranslation();
    const [lightboxOpen, setLightboxOpen] = React.useState(false);

    const selectedCount = selectedWallIds.length;
    const continueDisabled = !!pending || !!generating || selectedCount === 0;

    // Tint of the hover highlight — picked to read clearly against any
    // wall colour in either light or dark room scenes (cyan with
    // moderate alpha translates well over warm and cool walls alike).
    const highlightRgb: [number, number, number] = [99, 102, 241];
    const overlays = useWallMaskHighlight(maskUrl, walls, highlightRgb);

    // Continue button body — identical in the node footer and the lightbox.
    const continueContent = generating ? (
        <>
            <Loader2 className="size-3.5 animate-spin" />
            {t('CONTENT_STUDIO.GENERATING_BTN', 'Generating…')}
        </>
    ) : (
        <>
            <ChevronRight className="size-3.5" />
            {t('CONTENT_STUDIO.WALL_PICKER_CONTINUE', 'Continue')}
            {typeof continueCreditCost === 'number' && continueCreditCost > 0 && (
                <span className="opacity-85">{' · '}{continueCreditCost} cr</span>
            )}
        </>
    );

    return (
        <NodeShell
            nodeId={nodeId}
            x={x}
            y={y}
            width={WALL_PICKER_NODE_WIDTH}
            zoom={zoom}
            selected={selected}
            accent="#6366F1"
            dimmed={dimmed}
            label={t('CONTENT_STUDIO.NODE_WALL_PICKER', 'Pick walls')}
            onDrag={onDrag}
            onClick={onClick}
            pulse={pending || generating}
            addHandle
            hoverAffordance={
                onRemove ? (
                    <div className="flex flex-col gap-1.5">
                        <ActionPill
                            label={t('CONTENT_STUDIO.ACTION_REMOVE')}
                            icon={<SvgRemove />}
                            onClick={onRemove}
                            destructive
                        />
                    </div>
                ) : undefined
            }
        >
            <div className="flex flex-col">
                {/* Image preview area with overlaid wall markers. */}
                <div className="relative border-b">
                    <WallPickerImageView
                        baseImageUrl={baseImageUrl}
                        walls={walls}
                        imageWidth={imageWidth}
                        imageHeight={imageHeight}
                        selectedWallIds={selectedWallIds}
                        pending={pending}
                        onToggleWall={onToggleWall}
                        onImageClick={baseImageUrl ? () => setLightboxOpen(true) : undefined}
                        overlays={overlays}
                        markerSize={28}
                    />

                    {/* Counter chip in the top-left corner */}
                    {!pending && walls.length > 0 && (
                        <div className="pointer-events-none absolute top-2 left-2 bg-foreground/85 px-2 py-0.5 text-xs font-medium tracking-[0.02em] text-background">
                            {t(
                                'CONTENT_STUDIO.WALL_PICKER_SELECTED_COUNT',
                                '{{selected}} of {{total}} walls',
                                { selected: selectedCount, total: walls.length },
                            )}
                        </div>
                    )}
                </div>

                {/* Footer: Continue button — spawns a render-phase Generation. */}
                <Button
                    variant="outline"
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        if (!continueDisabled) onContinue();
                    }}
                    disabled={continueDisabled}
                    className="h-auto w-full gap-2 border-0 px-4 py-2.5 text-sm"
                >
                    {continueContent}
                </Button>
            </div>

            {/* Lightbox — full-size view for comfortable wall picking. */}
            <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
                <DialogContent
                    showCloseButton={false}
                    // A DEFINITE height, not just a cap.
                    //
                    // The picture sits in a `flex: 1` pane between a header
                    // and a footer, and its own box is sized in percentages.
                    // With only a max-height the dialog's height came from
                    // its content, the content's height came from the
                    // dialog, and the pane resolved that circle at zero —
                    // header and footer drawn, nothing in between. The one
                    // thing the lightbox exists for is seeing the room
                    // bigger, so it takes the room it is given.
                    className="flex h-[calc(100%-16px)] w-[calc(100%-16px)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none md:h-[calc(100%-64px)] md:w-[min(1200px,calc(100%-64px))]"
                >
                    {/* Header */}
                    <div className="flex shrink-0 items-center justify-between border-b px-5 py-3">
                        <DialogTitle className="text-sm font-medium text-foreground">
                            {t('CONTENT_STUDIO.WALL_PICKER_LIGHTBOX_TITLE', 'Pick walls to texture')}
                        </DialogTitle>
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-muted-foreground">
                                {t(
                                    'CONTENT_STUDIO.WALL_PICKER_SELECTED_COUNT',
                                    '{{selected}} of {{total}} walls',
                                    { selected: selectedCount, total: walls.length },
                                )}
                            </span>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => setLightboxOpen(false)}
                                aria-label={t('CONTENT_STUDIO.CANCEL', 'Close')}
                            >
                                <X className="size-4" />
                            </Button>
                        </div>
                    </div>

                    {/* Image area — fills the dialog body */}
                    <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-muted p-2 md:p-4">
                        <div
                            className="relative flex max-h-full max-w-full"
                            // No picture yet, so nothing to measure: fall back
                            // to the declared shape rather than collapsing to
                            // the nothing an empty <img> would size to.
                            style={baseImageUrl ? undefined : {
                                height: '100%',
                                aspectRatio: imageWidth > 0 && imageHeight > 0
                                    ? `${imageWidth} / ${imageHeight}`
                                    : '1 / 1',
                            }}
                        >
                            {/*
                              * The sizer: a real image, doing the fitting.
                              *
                              * Which axis to pin cannot be decided from the
                              * picture alone — a square one is "not portrait", so
                              * it pinned its width, and in a pane wider than it is
                              * tall that meant a height clamped away from the
                              * aspect and an overlay several times the size of the
                              * pane it sits in. The pane's shape is as much of the
                              * question as the picture's.
                              *
                              * An <img> with both maxima and auto sizing already
                              * answers that — it is `object-fit: contain` done by
                              * the browser, on the same URL the overlay paints, so
                              * the markers cannot land anywhere but on it. Hidden
                              * because the ImageView draws the visible copy.
                              */}
                            {baseImageUrl && (
                                <img
                                    src={baseImageUrl}
                                    alt=""
                                    aria-hidden
                                    className="invisible block h-auto max-h-full w-auto max-w-full"
                                />
                            )}
                            <div className="absolute inset-0">
                                <WallPickerImageView
                                    baseImageUrl={baseImageUrl}
                                    walls={walls}
                                    imageWidth={imageWidth}
                                    imageHeight={imageHeight}
                                    selectedWallIds={selectedWallIds}
                                    pending={pending}
                                    onToggleWall={onToggleWall}
                                    overlays={overlays}
                                    markerSize={40}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Footer — actions */}
                    <div className="flex shrink-0 items-center justify-end gap-3 border-t px-5 py-3">
                        <Button variant="outline" onClick={() => setLightboxOpen(false)}>
                            {t('CONTENT_STUDIO.WALL_PICKER_DONE', 'Done')}
                        </Button>
                        <Button
                            variant="outline"
                            disabled={continueDisabled}
                            onClick={() => {
                                if (continueDisabled) return;
                                setLightboxOpen(false);
                                onContinue();
                            }}
                            className="min-w-[140px] gap-2"
                        >
                            {continueContent}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </NodeShell>
    );
};
